<template>
    <div class="network-overview">
        <div class="analytics-hero mb-4" v-if="showHeroStats">
            <div class="analytics-hero-stat">
                <div class="hero-label">Active Programs</div>
                <div class="hero-value">{{ formatCompact(summary.totalPrograms) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Total Fees</div>
                <div class="hero-value">{{ formatSOL(summary.totalFees) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Total Users</div>
                <div class="hero-value">{{ formatCompact(summary.totalUsers) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Block Space</div>
                <div class="hero-value">{{ formatCompute(summary.totalCompute) }} CU</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Top Program Share</div>
                <div class="hero-value">{{ topProgramShare }}%</div>
                <div class="hero-subtitle">{{ topProgramName }}</div>
            </div>
        </div>
        <!-- Jito / MEV and Completion Status Row -->
        <div class="analytics-row cols-2 mb-4" v-if="networkMetrics && showJitoCompletion">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Jito MEV Adoption</div>
                </div>
                <div class="panel-body">
                    <div class="metrics-row">
                        <div class="metric-item">
                            <div class="metric-label">Jito Transactions</div>
                            <div class="metric-value text-gradient">{{ formatCompact(networkMetrics.jitoTransactions) }}</div>
                            <div class="metric-subtitle">{{ networkMetrics.jitoPercent }}% of network</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">Total Jito Tips</div>
                            <div class="metric-value" style="color: #f97316;">{{ formatSOL(networkMetrics.totalJitoTips) }}</div>
                            <div class="metric-subtitle">SOL paid to validators</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">Total Revenue</div>
                            <div class="metric-value" style="color: #22c55e;">{{ formatSOL(networkMetrics.totalRevenue) }}</div>
                            <div class="metric-subtitle">Fees + Tips</div>
                        </div>
                    </div>
                    <div class="jito-bar mt-3">
                        <div class="jito-bar-fill" :style="{ width: networkMetrics.jitoPercent + '%' }"></div>
                    </div>
                    <div class="jito-bar-label mt-1">
                        <span>Native ({{ (100 - networkMetrics.jitoPercent).toFixed(1) }}%)</span>
                        <span>Jito ({{ networkMetrics.jitoPercent }}%)</span>
                    </div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Completion</div>
                    <div class="panel-subtitle">Completed vs Reverted transactions</div>
                </div>
                <div class="panel-body">
                    <div class="metrics-row">
                        <div class="metric-item">
                            <div class="metric-label">Completed</div>
                            <div class="metric-value" style="color: #22c55e;">{{ formatCompact(networkMetrics.totalTransactions - networkMetrics.revertedTransactions) }}</div>
                            <div class="metric-subtitle">{{ networkMetrics.completionRate }}% completion</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">Reverted</div>
                            <div class="metric-value" style="color: #ef4444;">{{ formatCompact(networkMetrics.revertedTransactions) }}</div>
                            <div class="metric-subtitle">{{ networkMetrics.revertedPercent }}% of transactions</div>
                        </div>
                        <div class="metric-item">
                            <div class="metric-label">Reverted Compute</div>
                            <div class="metric-value" style="color: #9ca3af;">{{ formatCompute(networkMetrics.revertedCompute) }}</div>
                            <div class="metric-subtitle">CU on reverted tx</div>
                        </div>
                    </div>
                    <div class="completion-bar mt-3">
                        <div class="completion-bar-fill" :style="{ width: networkMetrics.completionRate + '%' }"></div>
                    </div>
                    <div class="completion-bar-label mt-1">
                        <span style="color: #22c55e;">Completed ({{ networkMetrics.completionRate }}%)</span>
                        <span style="color: #ef4444;">Reverted ({{ networkMetrics.revertedPercent }}%)</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCompact, formatCompute, formatSOL, formatPubkey } from '../../composables/useFormatters';

export default {
    name: 'NetworkOverview',

    props: {
        summary: {
            type: Object,
            default: () => ({
                totalPrograms: 0,
                totalFees: 0,
                totalUsers: 0,
                totalCompute: 0
            })
        },
        topProgram: {
            type: Object,
            default: null
        },
        interval: {
            type: String,
            default: '1h'
        },
        range: {
            type: String,
            default: '24h'
        },
        showJitoCompletion: {
            type: Boolean,
            default: true
        },
        showHeroStats: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            networkMetrics: null
        };
    },

    watch: {
        interval() {
            this.fetchNetworkMetrics();
        },
        range() {
            this.fetchNetworkMetrics();
        }
    },

    mounted() {
        this.fetchNetworkMetrics();
    },

    methods: {
        formatCompact,
        formatCompute,
        formatSOL,
        formatPubkey,

        async fetchNetworkMetrics() {
            try {
                const params = new URLSearchParams({
                    interval: this.interval,
                    range: this.range
                });
                const response = await fetch(`/analytics/api/fee-metrics?${params}`);
                const data = await response.json();
                this.networkMetrics = data.summary || null;
            } catch (error) {
                console.error('Failed to fetch network metrics:', error);
                this.networkMetrics = null;
            }
        }
    },

    computed: {
        topProgramName() {
            if (this.topProgram) {
                return this.formatPubkey(this.topProgram.program);
            }
            return '';
        },
        topProgramShare() {
            if (this.topProgram) {
                return this.topProgram.metrics.computeShare?.toFixed(1) || 0;
            }
            return 0;
        }
    }
};
</script>

<style scoped>
.hero-subtitle {
    font-size: 0.75rem;
    color: rgba(255,255,255,0.5);
    margin-top: 0.25rem;
    font-family: 'JetBrains Mono', monospace;
}

.metrics-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.metric-item {
    text-align: center;
}

.metric-label {
    font-size: 0.8rem;
    color: #9ca3af;
    margin-bottom: 4px;
}

.metric-value {
    font-size: 1.25rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
}

.metric-subtitle {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 2px;
}

.text-gradient {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.jito-bar {
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.jito-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #8b5cf6, #f97316);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.jito-bar-label {
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
    color: #9ca3af;
}

.completion-bar {
    height: 8px;
    background: rgba(239, 68, 68, 0.3);
    border-radius: 4px;
    overflow: hidden;
}

.completion-bar-fill {
    height: 100%;
    background: #22c55e;
    border-radius: 4px;
    transition: width 0.3s ease;
}

.completion-bar-label {
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
}
</style>
